import { useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode, type SyntheticEvent } from 'react'
import { Button, Checkbox, Chip, Input, Label, ListBox, Meter, ProgressBar, SearchField, Select, Table, TextField, ToggleButton, ToggleButtonGroup, Tooltip } from '@heroui/react'
import { api, ApiError, type PlaylistSummary, type SignageMedia } from '../lib/api'
import { filmFacts } from '../lib/filmFacts'
import { addEntries, hoverPlayer } from '../lib/playlistView'
import { filmLength, LIBRARY_ACCEPT, MAX_ENTRIES, mediaPreview } from '../lib/signageEdit'
import { MEDIA_SORTS, nextSelection, sizeOf, uploadRefusal, usageByMedia, viewMedia, type KindFilter, type MediaSort, type Usage } from '../lib/signageViews'
import { explain } from '../lib/staffErrors'
import { formatMoment, type StaffKey } from '../lib/staffText'
import { uploadEach, type UploadRow } from '../lib/uploadQueue'
import { Notice } from '../admin/settingsKit'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { showToast } from '../ui/toast'
import type { SignageData } from './signageData'
import { ConfirmModal, Empty, MoreMenu, SignageDrawer, SignageModal, TileSkeletons, UploadButton } from './signageKit'

const hover = hoverPlayer()

const hoverOn = (e: SyntheticEvent<HTMLElement>) => {
  const v = e.currentTarget.querySelector('video')
  if (v) hover.enter(v)
}
const hoverOff = (e: SyntheticEvent<HTMLElement>) => {
  const v = e.currentTarget.querySelector('video')
  if (v) hover.leave(v)
}

/** Handlers for whatever holds a Thumb: its film plays muted while the pointer or focus is on it. */
export const filmHover = { onMouseEnter: hoverOn, onMouseLeave: hoverOff, onFocus: hoverOn, onBlur: hoverOff }

/** A picture as itself, a film by its still frame (played on hover) or a mark. */
export function Thumb({
  media,
  className = 'signage-thumb',
}: {
  media: Pick<SignageMedia, 'assetId' | 'kind'> & { posterAssetId?: string }
  className?: string
}) {
  if (media.kind === 'image') {
    return (
      <span className={className}>
        <img src={api.assetURL(media.assetId)} alt="" loading="lazy" draggable={false} />
      </span>
    )
  }
  return (
    <span className={className}>
      {media.posterAssetId ? (
        <video src={api.assetURL(media.assetId)} poster={api.assetURL(media.posterAssetId)} preload="none" muted loop playsInline />
      ) : (
        <Icon name="play" size={16} />
      )}
    </span>
  )
}

/** Uploads a file to the library, measuring a film in this browser first; duplicate when it was already there. */
export async function uploadToLibrary(
  file: File,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<{ media: SignageMedia; duplicate?: boolean }> {
  const facts: { durationMs: number; poster?: Blob } = file.type.startsWith('video/') ? await filmFacts(file) : { durationMs: 0 }
  return api.uploadSignageMedia(file, {
    durationMs: facts.durationMs,
    ...(facts.poster ? { poster: facts.poster } : {}),
    ...(onProgress ? { onProgress } : {}),
    ...(signal ? { signal } : {}),
  })
}

const REFUSALS: Record<NonNullable<ReturnType<typeof uploadRefusal>>, StaffKey> = {
  type: 'signage.refuse.type',
  pictureSize: 'signage.refuse.pictureSize',
  filmSize: 'signage.refuse.filmSize',
}

/** Sends files to the library one at a time, each refused for its own reason; the batch stops when the view closes. */
export function useLibraryUpload(onSent: (m: SignageMedia) => void) {
  const { say, lang } = useStaffText()
  const [uploads, setUploads] = useState<UploadRow[]>([])
  const [sending, setSending] = useState(false)
  const batch = useRef<AbortController | null>(null)
  useEffect(() => () => batch.current?.abort(), [])
  const sentRef = useRef(onSent)
  sentRef.current = onSent
  const start = async (files: File[]) => {
    if (sending || files.length === 0) return
    setSending(true)
    const stop = new AbortController()
    batch.current = stop
    try {
      await uploadEach(files, {
        signal: stop.signal,
        accept: () => true,
        refusal: (f) => {
          const why = uploadRefusal(f)
          return why ? say(REFUSALS[why]) : undefined
        },
        send: async (f, progress, signal) => {
          const sent = await uploadToLibrary(f, progress, signal)
          sentRef.current(sent.media)
          return { duplicate: sent.duplicate, name: sent.media.name }
        },
        explain: (err) => explain(err, lang, 'signage.uploadFailed'),
        notAccepted: (name) => say('common.fileNotAccepted', { name }),
        onChange: setUploads,
      })
    } finally {
      batch.current = null
      setSending(false)
    }
  }
  return { uploads, sending, start, clear: () => setUploads([]) }
}

/** One file of a batch: its progress while it goes, then what became of it. */
export function UploadLine({ row }: { row: UploadRow }) {
  const { say } = useStaffText()
  const note =
    row.state === 'waiting'
      ? say('signage.upload.waiting')
      : row.state === 'sending'
        ? `${Math.round(row.progress * 100)}%`
        : row.state === 'done'
          ? say('signage.upload.done')
          : row.state === 'duplicate'
            ? say('signage.duplicate', { name: row.note ?? '' })
            : row.note
  const icon = row.state === 'done' || row.state === 'duplicate' ? 'check' : row.state === 'refused' ? 'alert' : 'upload'
  return (
    <li className="signage-upload" data-state={row.state}>
      <span className="signage-upload__icon" aria-hidden="true">
        <Icon name={icon} size={14} />
      </span>
      <span className="signage-upload__name" title={row.name}>
        {row.name}
      </span>
      {(row.state === 'waiting' || row.state === 'sending') && (
        <ProgressBar className="signage-upload__bar" size="sm" aria-label={row.name} value={row.progress} maxValue={1}>
          <ProgressBar.Track>
            <ProgressBar.Fill />
          </ProgressBar.Track>
        </ProgressBar>
      )}
      <span className={row.state === 'refused' ? 'signage-upload__refused' : 'signage-upload__note'}>{note}</span>
    </li>
  )
}

/** A batch's files, with Clear once nothing is still going. */
export function UploadList({ rows, sending, onClear }: { rows: UploadRow[]; sending: boolean; onClear: () => void }) {
  const { say } = useStaffText()
  if (rows.length === 0) return null
  return (
    <section className="signage-uploads" aria-label={say('signage.upload')}>
      <ul>
        {rows.map((row, i) => (
          <UploadLine key={`${i}:${row.name}`} row={row} />
        ))}
      </ul>
      {!sending && (
        <Button size="sm" variant="ghost" className="signage-uploads__clear" onPress={onClear}>
          {say('common.clear')}
        </Button>
      )}
    </section>
  )
}

/** Files dragged over target show the drop sheet; dropped files upload. */
export function useFileDrop(onFiles: (files: File[]) => void, off = false) {
  const [dropping, setDropping] = useState(false)
  const props = {
    'data-dropping': dropping || undefined,
    onDragOver: (e: DragEvent<HTMLElement>) => {
      if (!e.dataTransfer.types.includes('Files')) return
      // Prevented even while off, or the browser opens the file in place of the page.
      e.preventDefault()
      if (!off) setDropping(true)
    },
    onDragLeave: (e: DragEvent<HTMLElement>) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropping(false)
    },
    onDrop: (e: DragEvent<HTMLElement>) => {
      if (!e.dataTransfer.types.includes('Files')) return
      e.preventDefault()
      setDropping(false)
      if (!off) onFiles([...e.dataTransfer.files])
    },
  }
  return { dropping, props }
}

/** The sheet over a drop target while files are held over it. */
export function DropSheet() {
  const { say } = useStaffText()
  return (
    <div className="signage-dropsheet" aria-hidden="true">
      <Icon name="upload" size={28} />
      <span>{say('signage.dropHere')}</span>
    </div>
  )
}

const SORT_KEYS: Record<MediaSort, StaffKey> = {
  newest: 'signage.sort.newest',
  oldest: 'signage.sort.oldest',
  name: 'signage.sort.name',
  size: 'signage.sort.size',
  length: 'signage.sort.length',
}

const KINDS: ReadonlyArray<[KindFilter, StaffKey]> = [
  ['all', 'signage.kind.all'],
  ['image', 'signage.kind.pictures'],
  ['video', 'signage.kind.films'],
]

/** Files drawn at first, and added with each Show more. */
const SHOWN = 60

type View = 'grid' | 'list'
const VIEW_KEY = 'qms.signage.libraryView'
const storedView = (): View => {
  try {
    return window.localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'grid'
  } catch {
    return 'grid'
  }
}

/** A file's facts in a line: a picture's pixels or a film's length, then its size. */
function factsOf(m: SignageMedia): string {
  const shape = m.kind === 'video' ? filmLength(m) : m.width && m.height ? `${m.width}×${m.height}` : ''
  return [shape, sizeOf(m.bytes)].filter(Boolean).join(' · ')
}

function usedCount(u: Usage | undefined): number {
  return u?.playlists.length ?? 0
}

/** The organisation's library: find, sort, upload, tick several, details in a drawer. */
export function LibraryTab({
  data,
  onOpenPlaylist,
  onNewPlaylist,
  onCampaigns,
}: {
  data: SignageData
  onOpenPlaylist: (id: string) => void
  onNewPlaylist: (files: SignageMedia[]) => void
  onCampaigns: () => void
}) {
  const { say, lang } = useStaffText()
  const [find, setFind] = useState('')
  const [kind, setKind] = useState<KindFilter>('all')
  const [sort, setSort] = useState<MediaSort>('newest')
  const [view, setView] = useState<View>(storedView)
  const [chosen, setChosen] = useState<Set<string>>(new Set())
  const anchor = useRef<string | undefined>(undefined)
  const shift = useRef(false)
  const [details, setDetails] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<{ ids: string[]; versions?: number } | null>(null)
  const [adding, setAdding] = useState<SignageMedia[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState('')
  const up = useLibraryUpload((m) => {
    data.addMedia(m)
    void data.reload('media')
  })
  const drop = useFileDrop((files) => void up.start(files), up.sending)

  const usage = useMemo(() => usageByMedia(data.playlists, data.campaigns), [data.playlists, data.campaigns])
  const shown = useMemo(() => viewMedia(data.media, find, kind, sort), [data.media, find, kind, sort])
  // Tiles come in pages of SHOWN as the person asks for more; a filter starts again.
  const [limit, setLimit] = useState({ key: '', n: SHOWN })
  const filterKey = `${find}|${kind}|${sort}`
  const upTo = limit.key === filterKey ? limit.n : SHOWN
  const rows = shown.slice(0, upTo)
  const order = shown.map((m) => m.id)
  const byId = new Map(data.media.map((m) => [m.id, m]))
  const picked = [...chosen].map((id) => byId.get(id)).filter((m): m is SignageMedia => m !== undefined)
  const open = details ? byId.get(details) : undefined

  // Files gone from the library leave the selection.
  useEffect(() => {
    setChosen((c) => {
      const kept = new Set([...c].filter((id) => data.media.some((m) => m.id === id)))
      return kept.size === c.size ? c : kept
    })
  }, [data.media])

  const tick = (id: string) => {
    setChosen((c) => nextSelection(order, c, id, shift.current ? { anchor: anchor.current } : undefined))
    anchor.current = id
  }
  const chooseView = (v: View) => {
    setView(v)
    try {
      window.localStorage.setItem(VIEW_KEY, v)
    } catch {
      // A private window keeps the choice for this visit only.
    }
  }

  const remove = async (ids: string[], versions = 0) => {
    setBusy(true)
    setNotice('')
    let gone = 0
    let fault = ''
    try {
      for (const id of ids) {
        try {
          await api.deleteSignageMedia(id, versions)
          gone++
        } catch (err) {
          if (err instanceof ApiError && err.code === 'IN_OLD_VERSIONS' && ids.length === 1) {
            setDeleting({ ids, versions: Number(err.args?.n ?? 0) })
            return
          }
          fault = explain(err, lang, 'common.unreachable')
        }
      }
      setDeleting(null)
      if (gone > 0) {
        if (ids.includes(details ?? '')) setDetails(null)
        showToast(say('signage.deleted'))
      }
      if (fault) setNotice(ids.length > 1 ? say('signage.someKept', { n: ids.length - gone }) : fault)
      await data.reload('media', 'playlists')
    } finally {
      setBusy(false)
    }
  }

  const act = (m: SignageMedia, id: string) => {
    if (id === 'details') setDetails(m.id)
    if (id === 'add') setAdding([m])
    if (id === 'delete') setDeleting({ ids: [m.id] })
  }
  const menuOf = (m: SignageMedia) => (
    <MoreMenu
      className="signage-tile__menu"
      label={say('signage.actions', { name: m.name })}
      onAction={(id) => act(m, id)}
      actions={[
        { id: 'details', label: say('signage.details'), icon: 'info' },
        { id: 'add', label: say('signage.addToPlaylist'), icon: 'plus' },
        { id: 'delete', label: say('common.delete'), icon: 'trash', danger: true, disabled: usedCount(usage.get(m.id)) > 0 },
      ]}
    />
  )
  const tickOf = (m: SignageMedia, className: string) => (
    <span
      className={className}
      onPointerDownCapture={(e) => {
        shift.current = e.shiftKey
      }}
      onKeyDownCapture={(e) => {
        shift.current = e.shiftKey
      }}
    >
      <Checkbox variant="secondary" aria-label={say('signage.select', { name: m.name })} isSelected={chosen.has(m.id)} onChange={() => tick(m.id)}>
        <Checkbox.Control>
          <Checkbox.Indicator />
        </Checkbox.Control>
      </Checkbox>
    </span>
  )
  const usageLine = (m: SignageMedia) => {
    const n = usedCount(usage.get(m.id))
    return n > 0 ? say('signage.inPlaylists', { n }) : say('signage.notUsed')
  }

  const uploadButton = (variant: 'primary' | 'secondary' = 'primary') => (
    <UploadButton accept={LIBRARY_ACCEPT} label={say('signage.upload')} variant={variant} disabled={up.sending} onFiles={(files) => void up.start(files)} />
  )
  const deletable = picked.filter((m) => usedCount(usage.get(m.id)) === 0)
  const spaceWords = say('signage.space2', { used: sizeOf(data.usedBytes), limit: sizeOf(data.limitBytes) })
  const full = data.limitBytes > 0 ? data.usedBytes / data.limitBytes : 0

  return (
    <section className="signage-lib signage-droptarget" aria-label={say('signage.tab.library')} {...drop.props}>
      <div className="signage-bar">
        {chosen.size > 0 ? (
          <div className="signage-selbar" role="toolbar" aria-label={say('signage.nChosen', { n: chosen.size })}>
            <Button isIconOnly size="sm" variant="ghost" aria-label={say('common.clear')} onPress={() => setChosen(new Set())}>
              <Icon name="close" size={16} />
            </Button>
            <strong className="signage-selbar__count">{say('signage.nChosen', { n: chosen.size })}</strong>
            {chosen.size < shown.length && (
              <Button size="sm" variant="ghost" onPress={() => setChosen(new Set(order))}>
                {say('signage.chooseAll')}
              </Button>
            )}
            <span className="signage-bar__gap" />
            <Button size="sm" variant="secondary" onPress={() => setAdding(picked)}>
              <Icon name="plus" size={15} /> {say('signage.addToPlaylist')}
            </Button>
            <Button size="sm" variant="danger-soft" isDisabled={deletable.length === 0 || busy} onPress={() => setDeleting({ ids: deletable.map((m) => m.id) })}>
              <Icon name="trash" size={15} /> {deletable.length === picked.length ? say('common.delete') : say('signage.deleteN', { n: deletable.length })}
            </Button>
          </div>
        ) : (
          <>
            <SearchField className="signage-find" aria-label={say('signage.findFile')} value={find} onChange={setFind} variant="secondary">
              <SearchField.Group>
                <SearchField.SearchIcon />
                <SearchField.Input placeholder={say('signage.findFile')} />
                <SearchField.ClearButton />
              </SearchField.Group>
            </SearchField>
            <ToggleButtonGroup
              className="signage-seg"
              size="sm"
              selectionMode="single"
              disallowEmptySelection
              aria-label={say('signage.kind')}
              selectedKeys={[kind]}
              onSelectionChange={(keys) => setKind(([...keys][0] as KindFilter) ?? 'all')}
            >
              {KINDS.map(([value, key]) => (
                <ToggleButton key={value} id={value}>
                  {say(key)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Select className="signage-sort" variant="secondary" aria-label={say('signage.sort')} value={sort} onChange={(v) => v != null && setSort(v as MediaSort)}>
              <Select.Trigger>
                <Icon name="filter" size={14} />
                <Select.Value />
                <Select.Indicator />
              </Select.Trigger>
              <Select.Popover>
                <ListBox>
                  {MEDIA_SORTS.map((s) => (
                    <ListBox.Item key={s} id={s} textValue={say(SORT_KEYS[s])}>
                      {say(SORT_KEYS[s])}
                      <ListBox.ItemIndicator />
                    </ListBox.Item>
                  ))}
                </ListBox>
              </Select.Popover>
            </Select>
            <span className="signage-bar__gap" />
            <Meter className="signage-space" aria-label={say('signage.storage')} value={Math.min(data.usedBytes, data.limitBytes)} maxValue={data.limitBytes > 0 ? data.limitBytes : 1} size="sm" color={full > 0.9 ? 'danger' : full > 0.75 ? 'warning' : 'default'}>
                <Meter.Track className="signage-space__bar">
                  <Meter.Fill />
                </Meter.Track>
                <span className="signage-space__words">{spaceWords}</span>
              </Meter>
            <ToggleButtonGroup
              className="signage-seg signage-viewtoggle"
              size="sm"
              selectionMode="single"
              disallowEmptySelection
              aria-label={say('signage.view')}
              selectedKeys={[view]}
              onSelectionChange={(keys) => chooseView(([...keys][0] as View) ?? 'grid')}
            >
              <ToggleButton id="grid" isIconOnly aria-label={say('signage.view.grid')}>
                <Icon name="grid" size={15} />
              </ToggleButton>
              <ToggleButton id="list" isIconOnly aria-label={say('signage.view.list')}>
                <Icon name="table" size={15} />
              </ToggleButton>
            </ToggleButtonGroup>
            {uploadButton()}
          </>
        )}
      </div>

      <UploadList rows={up.uploads} sending={up.sending} onClear={up.clear} />
      {(notice || data.faults.media) && <Notice>{notice || data.faults.media}</Notice>}

      {!data.loaded.media ? (
        <TileSkeletons />
      ) : data.media.length === 0 ? (
        <div className="signage-dropzone">
          <Empty icon="upload" title={say('signage.library.empty')} line={say('signage.formats')} action={uploadButton()} />
        </div>
      ) : shown.length === 0 ? (
        <Empty icon="search" title={say('signage.noMatch', { q: find.trim() || say(KINDS.find((k) => k[0] === kind)![1]) })} />
      ) : view === 'grid' ? (
        <ul className="signage-grid" aria-label={say('signage.tab.library')}>
          {rows.map((m) => (
            <li key={m.id} className="signage-tile" data-chosen={chosen.has(m.id) || undefined} data-ticking={chosen.size > 0 || undefined}>
              <button type="button" className="signage-tile__face" aria-label={`${say('signage.details')}: ${m.name}`} onClick={() => setDetails(m.id)} {...filmHover}>
                <Thumb media={m} className="signage-face" />
                {m.kind === 'video' && (
                  <span className="signage-badge">
                    <Icon name="play" size={10} /> {filmLength(m) || say('signage.film')}
                  </span>
                )}
              </button>
              {tickOf(m, 'signage-tile__tick')}
              {menuOf(m)}
              <div className="signage-tile__body">
                <span className="signage-tile__name" title={m.name}>
                  {m.name}
                </span>
                <span className="signage-tile__meta">
                  <span>{factsOf(m)}</span>
                  <span className="signage-tile__used" data-used={usedCount(usage.get(m.id)) > 0 || undefined}>
                    {usageLine(m)}
                  </span>
                </span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <Table variant="secondary" className="signage-table signage-libtable">
          <Table.ScrollContainer>
            <Table.Content aria-label={say('signage.tab.library')} onRowAction={(key) => setDetails(String(key))}>
              <Table.Header>
                <Table.Column className="signage-libtable__tick">
                  <span className="sr-only">{say('signage.choose')}</span>
                </Table.Column>
                <Table.Column isRowHeader>{say('signage.name')}</Table.Column>
                <Table.Column>{say('signage.kind')}</Table.Column>
                <Table.Column>{say('signage.shapeOrLength')}</Table.Column>
                <Table.Column>{say('signage.sizeCol')}</Table.Column>
                <Table.Column>{say('signage.usedBy')}</Table.Column>
                <Table.Column>{say('signage.added')}</Table.Column>
                <Table.Column className="signage-libtable__menu">
                  <span className="sr-only">{say('signage.details')}</span>
                </Table.Column>
              </Table.Header>
              <Table.Body>
                {rows.map((m) => (
                  <Table.Row key={m.id} id={m.id}>
                    <Table.Cell>{tickOf(m, 'signage-libtable__check')}</Table.Cell>
                    <Table.Cell>
                      <span className="signage-libtable__name" {...filmHover}>
                        <Thumb media={m} className="signage-thumb" />
                        <span title={m.name}>{m.name}</span>
                      </span>
                    </Table.Cell>
                    <Table.Cell>{say(m.kind === 'video' ? 'signage.film' : 'signage.picture')}</Table.Cell>
                    <Table.Cell className="signage-num">{m.kind === 'video' ? filmLength(m) || '—' : m.width && m.height ? `${m.width}×${m.height}` : '—'}</Table.Cell>
                    <Table.Cell className="signage-num">{sizeOf(m.bytes)}</Table.Cell>
                    <Table.Cell>
                      <span className="signage-tile__used" data-used={usedCount(usage.get(m.id)) > 0 || undefined}>
                        {usageLine(m)}
                      </span>
                    </Table.Cell>
                    <Table.Cell className="signage-num">{formatMoment(m.createdAt, lang, { date: true })}</Table.Cell>
                    <Table.Cell>{menuOf(m)}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
      )}
      {shown.length > upTo && (
        <Button variant="secondary" size="sm" className="signage-more-files" onPress={() => setLimit({ key: filterKey, n: upTo + SHOWN })}>
          {say('signage.showMore', { n: shown.length - upTo })}
        </Button>
      )}

      {drop.dropping && <DropSheet />}
      {open && (
        <MediaDetails
          media={open}
          usage={usage.get(open.id)}
          onClose={() => setDetails(null)}
          onRenamed={() => void data.reload('media', 'playlists')}
          onDelete={() => setDeleting({ ids: [open.id] })}
          onAdd={() => setAdding([open])}
          onOpenPlaylist={onOpenPlaylist}
          onCampaigns={onCampaigns}
        />
      )}
      {deleting && (
        <ConfirmModal
          title={
            deleting.versions
              ? say('designs.media.alsoVersions', { n: deleting.versions })
              : deleting.ids.length === 1
                ? say('signage.deleteOne', { name: byId.get(deleting.ids[0]!)?.name ?? '' })
                : say('signage.deleteMany', { n: deleting.ids.length })
          }
          confirm={say('common.delete')}
          busy={busy}
          onClose={() => setDeleting(null)}
          onConfirm={() => void remove(deleting.ids, deleting.versions ?? 0).then(() => setChosen(new Set()))}
        >
          {deleting.ids.length < picked.length && chosen.size > 0 && <p className="signage-confirm__line">{say('signage.inUseStay', { n: picked.length - deleting.ids.length })}</p>}
        </ConfirmModal>
      )}
      {adding && (
        <AddToPlaylist
          files={adding}
          playlists={data.playlists}
          library={data.media}
          onClose={() => setAdding(null)}
          onNew={() => {
            setAdding(null)
            setChosen(new Set())
            onNewPlaylist(adding)
          }}
          onAdded={() => {
            setAdding(null)
            setChosen(new Set())
            void data.reload('playlists')
          }}
        />
      )}
    </section>
  )
}

/** One fact of the details drawer. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="signage-fact">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  )
}

/** A library file whole: preview, name, facts, where it plays, and its actions. */
function MediaDetails({
  media: m,
  usage,
  onClose,
  onRenamed,
  onDelete,
  onAdd,
  onOpenPlaylist,
  onCampaigns,
}: {
  media: SignageMedia
  usage?: Usage
  onClose: () => void
  onRenamed: () => void
  onDelete: () => void
  onAdd: () => void
  onOpenPlaylist: (id: string) => void
  onCampaigns: () => void
}) {
  const { say, lang } = useStaffText()
  const [name, setName] = useState(m.name)
  const [saving, setSaving] = useState(false)
  const [fault, setFault] = useState('')
  const [pixels, setPixels] = useState<{ width: number; height: number } | null>(m.width && m.height ? { width: m.width, height: m.height } : null)
  useEffect(() => {
    setName(m.name)
    setFault('')
    setPixels(m.width && m.height ? { width: m.width, height: m.height } : null)
  }, [m.id, m.name, m.width, m.height])
  const shown = mediaPreview(m, api.assetURL)
  const changed = name.trim() !== m.name && name.trim() !== ''
  const rename = async () => {
    if (!changed) return
    setSaving(true)
    setFault('')
    try {
      await api.renameSignageMedia(m.id, name.trim())
      showToast(say('common.saved'))
      onRenamed()
    } catch (err) {
      setFault(explain(err, lang, 'common.unreachable'))
    } finally {
      setSaving(false)
    }
  }
  const inUse = (usage?.playlists.length ?? 0) > 0
  return (
    <SignageDrawer
      title={m.name}
      onClose={onClose}
      foot={
        <>
          {inUse ? (
            <Tooltip delay={0}>
              <Button variant="danger-soft" isDisabled className="signage-drawer__danger">
                <Icon name="trash" size={15} /> {say('common.delete')}
              </Button>
              <Tooltip.Content>{say('signage.inUse')}</Tooltip.Content>
            </Tooltip>
          ) : (
            <Button variant="danger-soft" className="signage-drawer__danger" onPress={onDelete}>
              <Icon name="trash" size={15} /> {say('common.delete')}
            </Button>
          )}
          <Button variant="secondary" onPress={onAdd}>
            <Icon name="plus" size={15} /> {say('signage.addToPlaylist')}
          </Button>
        </>
      }
    >
      <div className="signage-details__preview">
        {shown.kind === 'video' ? (
          <video
            key={m.id}
            src={shown.src}
            poster={shown.poster}
            controls
            muted
            playsInline
            onLoadedMetadata={(e) => setPixels({ width: e.currentTarget.videoWidth, height: e.currentTarget.videoHeight })}
          />
        ) : (
          <img key={m.id} src={shown.src} alt="" onLoad={(e) => pixels ?? setPixels({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight })} />
        )}
      </div>
      <form
        className="signage-details__name"
        onSubmit={(e) => {
          e.preventDefault()
          void rename()
        }}
      >
        <TextField value={name} maxLength={80} onChange={setName} isInvalid={!!fault} className="signage-field">
          <Label>{say('signage.name')}</Label>
          <Input variant="secondary" onBlur={() => void rename()} />
        </TextField>
        {changed && (
          <Button type="submit" size="sm" variant="primary" isPending={saving}>
            {say('common.save')}
          </Button>
        )}
      </form>
      {fault && <Notice>{fault}</Notice>}
      <dl className="signage-facts">
        <Fact label={say('signage.kind')}>{say(m.kind === 'video' ? 'signage.film' : 'signage.picture')}</Fact>
        <Fact label={say('signage.pixels')}>{pixels ? `${pixels.width} × ${pixels.height}` : '—'}</Fact>
        {m.kind === 'video' && <Fact label={say('signage.length')}>{filmLength(m) || '—'}</Fact>}
        <Fact label={say('signage.sizeCol')}>{sizeOf(m.bytes)}</Fact>
        <Fact label={say('signage.added')}>{formatMoment(m.createdAt, lang, { date: true, time: true })}</Fact>
      </dl>
      <div className="signage-details__used">
        <span className="signage-label">{say('signage.usedBy')}</span>
        {!inUse && (usage?.designs.length ?? 0) === 0 ? (
          <span className="signage-muted">{say('signage.notUsed')}</span>
        ) : (
          <ul className="signage-uses">
            {usage?.playlists.map((p) => (
              <li key={p.id}>
                <button type="button" className="signage-use" onClick={() => onOpenPlaylist(p.id)}>
                  <Icon name="play" size={14} />
                  <span>{p.name}</span>
                  <Icon name="right" size={14} />
                </button>
              </li>
            ))}
            {usage?.campaigns.map((c) => (
              <li key={c.id}>
                <button type="button" className="signage-use" onClick={onCampaigns}>
                  <Icon name="clock" size={14} />
                  <span>{c.name}</span>
                  <Icon name="right" size={14} />
                </button>
              </li>
            ))}
            {usage?.designs.map((d) => (
              <li key={d} className="signage-use signage-use--plain">
                <Icon name="designs" size={14} />
                <span>{d}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </SignageDrawer>
  )
}

/** Files added to the end of a saved playlist, or to a new one. */
function AddToPlaylist({
  files,
  playlists,
  library,
  onClose,
  onNew,
  onAdded,
}: {
  files: SignageMedia[]
  playlists: PlaylistSummary[]
  library: SignageMedia[]
  onClose: () => void
  onNew: () => void
  onAdded: () => void
}) {
  const { say, lang } = useStaffText()
  const [target, setTarget] = useState('')
  const [busy, setBusy] = useState(false)
  const [fault, setFault] = useState('')
  const chosen = playlists.find((p) => p.id === target)
  const room = chosen ? MAX_ENTRIES - chosen.entries.length : MAX_ENTRIES
  const add = async () => {
    if (!chosen) return
    setBusy(true)
    setFault('')
    try {
      const { playlist } = await api.playlist(chosen.id)
      const entries = addEntries(playlist.entries, files).slice(0, MAX_ENTRIES)
      await api.savePlaylist(playlist.id, { name: playlist.name, version: playlist.version, entries, ticker: playlist.ticker })
      showToast(say('signage.addedTo', { name: playlist.name }))
      onAdded()
    } catch (err) {
      setFault(explain(err, lang, 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }
  return (
    <SignageModal
      title={files.length === 1 ? say('signage.addOne', { name: files[0]!.name }) : say('signage.addMany', { n: files.length })}
      onClose={onClose}
      footNote={
        <Button variant="ghost" onPress={onNew}>
          <Icon name="plus" size={15} /> {say('signage.newPlaylist')}
        </Button>
      }
      foot={
        <>
          <Button variant="tertiary" onPress={onClose}>
            {say('common.cancel')}
          </Button>
          <Button variant="primary" isDisabled={!chosen || room < files.length} isPending={busy} onPress={() => void add()}>
            {say('common.add')}
          </Button>
        </>
      }
    >
      {fault && <Notice>{fault}</Notice>}
      {playlists.length === 0 ? (
        <Empty icon="play" title={say('signage.playlists.empty')} />
      ) : (
        <ul className="signage-choose" role="listbox" aria-label={say('signage.tab.playlists')}>
          {playlists.map((p) => {
            const first = library.find((m) => m.id === p.entries[0]?.mediaItemId)
            const full = MAX_ENTRIES - p.entries.length < files.length
            return (
              <li key={p.id}>
                <button type="button" role="option" aria-selected={target === p.id} disabled={full} className="signage-choose__row" onClick={() => setTarget(p.id)}>
                  {first ? <Thumb media={first} className="signage-thumb" /> : <span className="signage-thumb"><Icon name="play" size={14} /></span>}
                  <span className="signage-choose__name">{p.name}</span>
                  <span className="signage-muted">{full ? say('signage.problem.tooMany') : say('signage.nItems', { n: p.entries.length })}</span>
                  {target === p.id && <Icon name="check" size={16} />}
                </button>
              </li>
            )
          })}
        </ul>
      )}
      {chosen && room < files.length && <Chip color="warning" variant="soft">{say('signage.problem.tooMany')}</Chip>}
    </SignageModal>
  )
}
